/**
 * RealisticWaterMaterial — the "hyper-realistic" third water type: a TSL node
 * material combining every technique a modern real-time water needs, on top
 * of the same Gerstner wave set the CPU height query uses.
 *
 * Technique sources (researched before implementation):
 *  - Gerstner displacement + analytic normals: GPU Gems ch. 1 (Finch 2004),
 *    https://developer.nvidia.com/gpugems/gpugems/part-i-natural-effects/chapter-1-effective-water-simulation-physical-models
 *  - Screen-space refraction + planar reflection node idiom: three.js r182
 *    Water2Mesh (examples/jsm/objects/Water2Mesh.js) — `reflector()` with an
 *    offset uvNode, `adaptiveSceneColor(viewportSafeUV(screenUV + offset))`.
 *    The reflector's mirror plane is its `target`'s local XY plane (normal
 *    +Z, ReflectorNode.updateBefore); three's examples rotate the MESH so a
 *    child target inherits an up normal — this sheet bakes the rotation into
 *    the geometry, so the target is pre-rotated here (see `reflectorTarget`).
 *  - Wavelength-dependent Beer–Lambert absorption (red extinguishes first)
 *    over the view-space water thickness from the depth buffer: Ben Sims,
 *    "Physically Plausible, Simplified Water Shading" (2021),
 *    https://www.ben-sims.com/2021/02/13/physically-plausible-simplified-water-shading/
 *  - Depth-difference shoreline foam + animated Voronoi caustics modulating
 *    the refracted floor: Cyanilux "Water Shader Breakdown"
 *    (https://www.cyanilux.com/tutorials/water-shader-breakdown/) and
 *    GPU Gems ch. 2 "Rendering Water Caustics" (aesthetics-driven approximation).
 *  - Schlick Fresnel with F0 ≈ 0.02 (water IOR 1.33).
 *  - Underside rendering: Snell's window — from below, rays within the
 *    critical angle (sin θc = 1/1.33 → θc ≈ 48.75°, cos θc ≈ 0.66) transmit
 *    the above-water scene; beyond it, total internal reflection shows only
 *    the dark water body (standard real-time approximation).
 *
 * The material is UNLIT by design (MeshBasicNodeMaterial): every lighting
 * term — sun specular, sky/reflection energy, subsurface tint — is authored
 * here, which is how the reference implementations behave and keeps the
 * result stable across the WebGPU and WebGL2 backends.
 */

import * as THREE from 'three/webgpu';
import {
  Discard,
  Fn,
  attribute,
  color,
  cameraPosition,
  clamp,
  cos,
  dot,
  exp,
  float,
  frontFacing,
  length,
  max,
  min,
  mix,
  sin,
  mx_noise_float,
  mx_worley_noise_float,
  normalize,
  pmremTexture,
  pow,
  reflect,
  reflector,
  screenUV,
  smoothstep,
  texture,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
  viewportLinearDepth,
  viewportSafeUV,
  linearDepth,
  positionLocal,
  positionPrevious,
  positionWorld,
} from 'three/tsl';

import { adaptiveSceneColor } from './adaptiveViewportTexture.js';

import { filterVertexWaves, maxAmplitude, type GerstnerWave } from './GerstnerWaves.js';
import { makeTslGerstner, type TslWaveModulation } from './tsl-gerstner.js';
import { rainRingSlope } from './rain-rings.js';

/**
 * Owner-supplied sea-state uniforms (config.seaState as an object): the
 * owner creates them so a CausticsProjector built BEFORE the material can
 * share the same modulation nodes (its light map is bound at material build).
 */
export interface SeaStateUniforms {
  ampScale: ReturnType<typeof uniform<number>>;
  dirRot: ReturnType<typeof uniform<number>>;
}

export interface RealisticWaterMaterialConfig {
  /** Shallow-water tint (what thin water adds over the floor). */
  shallowColor: THREE.ColorRepresentation;
  /** Deep-water color (what the column converges to as light dies). */
  deepColor: THREE.ColorRepresentation;
  /**
   * Beer–Lambert extinction per channel, 1/world-unit. Water kills red first,
   * then green — a reddish extinction vector, so the residual light shifts
   * blue-green with depth (Sims 2021).
   */
  absorption: { r: number; g: number; b: number };
  /** Screen-space refraction distortion strength. */
  distortionScale: number;
  /**
   * Sample the live viewport copy for refraction / Snell's window (default
   * true). Set FALSE on the WebGPU backend: r182's copyFramebufferToTexture
   * ends + re-begins the active render pass per copy, and its per-context
   * encoder state goes stale across frames/resizes (already-ended passes,
   * finished encoders, destroyed attachments in submits → the setPipeline/
   * createBindGroup collapse, live-traced 2026-07-31). The fallback renders
   * depth-tinted transmission (shallow-color floor, flat-sky Snell window) —
   * no scene copy at all.
   */
  screenSpaceRefraction?: boolean;
  /** Foam color. */
  foamColor: THREE.ColorRepresentation;
  /** Water-thickness (view depth difference) covered by shoreline foam. */
  foamShoreDepth: number;
  /** Fraction of total crest amplitude where crest foam starts (0..1). */
  foamCrestStart: number;
  /**
   * Jacobian whitecap threshold: foam ramps in as the surface-compression
   * Jacobian falls below this (folding crests sit near/below 0). Default 0.6.
   */
  foamJacobianThreshold: number;
  /**
   * Persistent foam-trail texture (FoamAccumulator) sampled in plane UV.
   * Null/omitted = instantaneous Jacobian foam only.
   */
  foamAccumTexture?: THREE.Texture | null;
  /** Caustic shimmer intensity on the refracted floor (0 disables). */
  causticIntensity: number;
  /** Caustic pattern world scale. */
  causticScale: number;
  /**
   * Projected caustic light map (CausticsProjector, plane-UV addressed).
   * When set it replaces the Voronoi shimmer with the real refracted-ray
   * pattern.
   */
  causticTexture?: THREE.Texture | null;
  /** Planar reflections on/off (extra render pass when on). */
  reflection: boolean;
  /** Reflection render-target scale relative to the viewport. */
  reflectionResolutionScale: number;
  /** Sky color used instead of the reflector when reflection is off. */
  skyColor: THREE.ColorRepresentation;
  /** Sun specular color. */
  sunColor: THREE.ColorRepresentation;
  /** Blinn specular exponent for the sun glint (legacy; GGX path ignores it). */
  sunShininess: number;
  /**
   * Microfacet roughness for the GGX sun glint (0.02 mirror-calm … 0.3
   * churned). Governs the long grazing-angle glitter stretch. Default 0.12.
   */
  sunRoughness: number;
  /**
   * Environment map (PMREM-compatible) sampled for the sky side when planar
   * `reflection` is off — IBL instead of the flat skyColor. Null = flat color.
   */
  envMap?: THREE.Texture | null;
  /**
   * Interactive ripple field (RippleField texture: r=height, g=dh/dx,
   * b=dh/dz in plane UV) — adds displacement + normal detail on top of the
   * Gerstner waves. Null/omitted = no interactive ripples.
   */
  rippleTexture?: THREE.Texture | null;
  /**
   * Camera-following INTERACTION field (2026-09-11): a RippleField whose
   * origin follows the viewer, addressed in WORLD XZ over `size` metres
   * around `uInteractionCenter` (handles) — r = height, g = dh/dx,
   * b = dh/dz. Wakes, splashes, wading rings and rain live here; the plane-
   * UV `rippleTexture` stays for fixed-footprint ponds. Added AFTER the
   * shoaling attenuation (a wading ring in 30 cm of water must not die
   * with the swell), faded over the window's outer 10 %. Null = none.
   */
  interaction?: { texture: THREE.Texture; size: number } | null;
  /** Foam raised by steep interaction slopes (wakes, splashes); 0 = none. Default 0.6. */
  interactionFoam?: number;
  /**
   * Expose the runtime SEA-STATE uniforms (`uWaveAmpScale`, `uWaveDirRot`):
   * the wind scales the whole wave field and turns its heading with no
   * shader rebuild (tsl-gerstner's modulated evaluators; the CPU samplers
   * take the same `WaveModulation`). Default false: constants, folded.
   */
  seaState?: boolean | SeaStateUniforms;
  /**
   * Raindrop rings on the surface (rain-rings.ts): the `uRainIntensity`
   * uniform (handles) fades them in — 0 costs one uniform compare's worth of
   * shader (the rings still evaluate; keep it off on tiers that never rain
   * by omitting the flag). Default false.
   */
  rainRipples?: boolean;
  /**
   * FFT ocean textures (OceanFFT): when present the surface displaces from
   * the cascade textures instead of the analytic Gerstner sum —
   * displacement = (dx, h, dz), derivatives = (slopeX, slopeZ, Jacobian).
   */
  fft?: {
    displacement: THREE.Texture[];
    derivatives: THREE.Texture[];
    patchSizes: number[];
  } | null;
  /**
   * Infinite-ocean displacement fade: waves flatten between start and end
   * metres from the mesh center (the camera) so the sparse horizon grid
   * never aliases. Null = no fade (finite bodies).
   */
  distanceFade?: { start: number; end: number } | null;
  /**
   * Terrain-aware shoreline data (ShorelineField): bed depth below the calm
   * surface, plane-UV addressed. Enables wave SHOALING (displacement fades
   * to zero as the bed rises — swell dies on the beach), a shallow sand
   * tint, and a surf-foam band that works from any viewpoint (the
   * depth-buffer shore foam needs the floor on screen).
   */
  shoreline?: {
    /** R = bedDepth/encodeMax, plane-UV addressed (v flips like foam). */
    depthTexture?: THREE.Texture | null;
    /**
     * Bed depth from a per-vertex FLOAT attribute (metres of water under the
     * surface at the vertex) instead of the plane-UV bake — for finite strips
     * such as river ribbons, which have no bake footprint. Drives the same
     * shoaling, surf-foam band and shallow tint; `encodeMax` is ignored.
     */
    depthAttribute?: string;
    /**
     * Carve the surface along the zero-depth contour (the sea's waterline
     * discard). Default: on with a depth texture, OFF with an attribute — a
     * strip keeps its lip so the water still meets a coarse cut bank.
     */
    waterlineDiscard?: boolean;
    /** Metres encoded at R = 1. */
    encodeMax: number;
    /** Depth below which wave displacement attenuates. */
    shoalDepth: number;
    /** Bed depth covered by the surf-foam band. */
    foamDepth: number;
    /** Shallow tint pulled in as the bed nears the surface (null = off). */
    shallowTint: THREE.ColorRepresentation | null;
  } | null;
  /**
   * Strip advection (rivers): the detail normals ride the mesh's own METRIC
   * uv (u = metres along the flow, v = metres across) and travel toward +u
   * at `speed` m/s — the pattern goes downstream, not with the wind. Null /
   * omitted = world-XZ detail (the sea). `uvScale` defaults to `normalScale`
   * so the texel density matches the sea's.
   */
  flow?: { speed: number; uvScale?: number } | null;
  /** Detail normal-map (tiling water normals); repeats per world unit. */
  normalMap: THREE.Texture;
  normalScale: number;
  /** Detail normal scroll speed. */
  normalSpeed: number;
  /**
   * Shortest wavelength the VERTEX stage displaces, in metres (Gerstner
   * mode). A displacement grid folds every wavelength below its Nyquist
   * limit (2× the vertex spacing) into pseudo-random per-vertex phases —
   * each quad tilts independently and the sea reads as a checkerboard of
   * flat tiles. Set this to ~2.5× the mesh's vertex spacing so geometry
   * carries only the swell it can sample; the shorter chop still renders
   * through the per-pixel analytic normal, Jacobian foam and detail maps,
   * none of which alias on the mesh. 0/omitted = displace every wave
   * (correct for fine grids where spacing ≪ the shortest wavelength).
   */
  vertexWavelengthCutoff?: number;
}

export const DEFAULT_REALISTIC_WATER: Omit<
  RealisticWaterMaterialConfig,
  'normalMap' | 'fft' | 'rippleTexture' | 'foamAccumTexture' | 'shoreline'
> = {
  shallowColor: 0x2e8b8b,
  deepColor: 0x03212e,
  absorption: { r: 0.9, g: 0.28, b: 0.16 },
  distortionScale: 0.045,
  foamColor: 0xeef6f8,
  foamShoreDepth: 0.55,
  foamCrestStart: 0.68,
  foamJacobianThreshold: 0.6,
  causticIntensity: 0.35,
  causticScale: 0.55,
  reflection: true,
  reflectionResolutionScale: 0.5,
  skyColor: 0x87a8c3,
  sunColor: 0xfff4e0,
  sunShininess: 220,
  sunRoughness: 0.12,
  normalScale: 0.16,
  normalSpeed: 0.06,
};

export interface RealisticWaterHandles {
  material: THREE.MeshBasicNodeMaterial;
  /** Drive this from the owning system — CPU height queries use the SAME clock. */
  uTime: { value: number };
  /**
   * Seconds since the previous frame. The vertex stage evaluates the wave
   * field at `uTime − uFrameDt` to emit `positionPrevious`, so motion blur and
   * temporal passes see the waves' REAL velocity instead of their whole
   * displacement. Defaults to 1/60; an owner that never sets it is merely
   * slightly off under a variable frame rate, never wrong.
   */
  uFrameDt: { value: number };
  uSunDirection: { value: THREE.Vector3 };
  /** Sea state (config.seaState): amplitude scale of the whole wave field (1 = authored). */
  uWaveAmpScale: { value: number };
  /** Sea state: rotation of every wave heading, radians. */
  uWaveDirRot: { value: number };
  /** The sea-state nodes for a CausticsProjector sharing this surface (null without seaState). */
  waveModulationNodes: TslWaveModulation | null;
  /** World XZ centre of the interaction field (config.interaction) — the RippleField's origin, in the mesh's world frame. */
  uInteractionCenter: { value: THREE.Vector2 };
  /** Jacobian whitecap threshold (initialised from config.foamJacobianThreshold; the wind raises it). */
  uFoamJacobian: { value: number };
  /** Detail-normal scroll direction × speed (default (1, 0.8)); point it downwind. */
  uDetailDrift: { value: THREE.Vector2 };
  /** Rain on the surface, 0..1 (config.rainRipples). */
  uRainIntensity: { value: number };
  /** Mesh world XZ offset so wave phase is world-anchored (sync on move). */
  uWorldOffset: { value: THREE.Vector2 };
  /**
   * Scene illumination 0..1 (1 = full day). Scales every AUTHORED radiance
   * term (deep scatter, flat sky fallback, foam, caustics, sun glint) so the
   * water follows the day/night cycle instead of glowing its daylight colors
   * at midnight. Drive from the sun elevation (WaterSystem does when a
   * DayNightCycle is present); keep a small floor (~0.04) for moonlit seas.
   */
  uIlluminance: { value: number };
  /**
   * Sky-side fallback tint (only sampled when `reflection` is off and no
   * `envMap` is set). Drive with the time-of-day sky tint so the sea
   * reflects sunsets/night instead of a constant day blue.
   */
  uSkyColor: { value: THREE.Color };
  /**
   * Reflector target that MUST be added as a child of the water mesh (null
   * when `reflection` is off). It arrives PRE-ORIENTED: three's reflector
   * mirrors the scene across the plane through this object whose normal is
   * the object's LOCAL +Z, and this material is a horizontal sheet with the
   * rotation baked into the geometry (rotateX(-π/2)), so the target is
   * rotated −π/2 about X to make that +Z the mesh's +Y. Do not rotate it
   * again; move it (position.y) only when the surface is not at the mesh's
   * local y = 0 (per-vertex lake levels).
   */
  reflectorTarget: THREE.Object3D | null;
  /**
   * The reflector node itself (null when `reflection` is off). Diagnostics:
   * `reflectorNode.reflector.hasOutput` is false on any frame the camera
   * sat under the mirror plane (three clears the reflection to black then).
   */
  reflectorNode: ReturnType<typeof reflector> | null;
}

/**
 * Build the node material for a wave set. The wave parameters are baked as
 * shader constants (they define the sea state; changing them rebuilds the
 * material) while time / sun / offset stay uniforms.
 */
export function createRealisticWaterMaterial(
  config: RealisticWaterMaterialConfig,
  waves: GerstnerWave[],
): RealisticWaterHandles {
  const uTime = uniform(0);
  const uSunDirection = uniform(new THREE.Vector3(0.4, 0.8, 0.2).normalize());
  const uWorldOffset = uniform(new THREE.Vector2(0, 0));
  const uIlluminance = uniform(1);
  const uSkyColor = uniform(new THREE.Color(config.skyColor));
  const ownSea = typeof config.seaState === 'object' && config.seaState !== null ? config.seaState : null;
  const uWaveAmpScale = ownSea ? ownSea.ampScale : uniform(1);
  const uWaveDirRot = ownSea ? ownSea.dirRot : uniform(0);
  const uFoamJacobian = uniform(config.foamJacobianThreshold);
  const uDetailDrift = uniform(new THREE.Vector2(1, 0.8));
  const uInteractionCenter = uniform(new THREE.Vector2(0, 0));
  const uRainIntensity = uniform(0);
  // Sea state: the same amplitude scale + heading rotation the CPU samplers
  // apply (GerstnerWaves.WaveModulation), as uniform nodes — cos/sin once.
  const modNodes: TslWaveModulation | null = config.seaState
    ? { ampScale: uWaveAmpScale as unknown as ReturnType<typeof float>, dirCos: cos(uWaveDirRot), dirSin: sin(uWaveDirRot) }
    : null;

  const totalAmp = Math.max(maxAmplitude(waves), 1e-4);
  // The crest-foam band scales with the sea state (the authored total amplitude is a constant).
  const ampScaleNode = modNodes ? modNodes.ampScale : float(1);

  // ── Gerstner sum helpers (shared with the caustics projector) ────────────
  const gerstner = makeTslGerstner(waves, uTime, modNodes ?? undefined);
  // Vertex-stage wave set: drop wavelengths the grid cannot sample (they
  // alias into per-quad tile noise — see vertexWavelengthCutoff docs). The
  // FRAGMENT stages keep the FULL set: normals/foam/jacobian evaluate per
  // pixel, so short chop stays visible without touching the geometry.
  const vertexWaves = filterVertexWaves(waves, config.vertexWavelengthCutoff ?? 0);
  const gerstnerVertex =
    vertexWaves.length === waves.length ? gerstner : makeTslGerstner(vertexWaves, uTime, modNodes ?? undefined);
  // PREVIOUS-FRAME evaluator: the same wave set on a time uniform one frame
  // behind, so the vertex stage can emit a true `positionPrevious`. Without it
  // three defaults positionPrevious to the UNDISPLACED geometry, making every
  // vertex's velocity equal to the WHOLE displacement — with motion blur or a
  // temporal pass on, a still camera smears the entire sea (the positionPrevious
  // LAW; the same defect HierarchicalWind fixed for foliage). 2026-09-15.
  // Owners only drive `uFrameDt` (seconds since the last frame); the previous
  // time is derived in the shader, so an owner that never sets it still gets a
  // sane 1/60 s rather than a bogus velocity.
  const uFrameDt = uniform(1 / 60);
  const gerstnerVertexPrev = makeTslGerstner(vertexWaves, uTime.sub(uFrameDt) as never, modNodes ?? undefined);

  // Interaction window: world XZ → [0,1]² over the field, with a 10 % rim fade
  // so the window's edge never draws a seam (the field follows the viewer).
  const interaction = config.interaction ?? null;
  const interactionUv = (px: ReturnType<typeof float>, pz: ReturnType<typeof float>) =>
    vec2(px, pz).sub(uInteractionCenter).div(interaction!.size).add(0.5);
  const interactionEdge = (iuv: ReturnType<typeof interactionUv>) =>
    smoothstep(0, 0.1, min(min(iuv.x, float(1).sub(iuv.x)), min(iuv.y, float(1).sub(iuv.y))));

  const material = new THREE.MeshBasicNodeMaterial();

  // Bed depth (metres under the calm surface): the plane-UV bake, or a vertex
  // attribute for finite strips (river ribbons carry no bake footprint).
  const shore = config.shoreline ?? null;
  const bedDepthNode = (): ReturnType<typeof float> | null => {
    if (!shore) return null;
    if (shore.depthAttribute) return attribute(shore.depthAttribute, 'float') as ReturnType<typeof float>;
    if (shore.depthTexture) {
      return texture(shore.depthTexture, vec2(uv().x, float(1).sub(uv().y))).r.mul(shore.encodeMax);
    }
    return null;
  };

  // ── Vertex: displace the grid by the wave field (world-anchored phase),
  // plus the interactive ripple height (plane-UV texture; v flips because
  // rotateX(-π/2) maps local z = −y).
  // FFT mode: displacement/derivatives come from the cascade textures the
  // compute pipeline refreshes each frame (world-anchored UV per patch).
  const fft = config.fft ?? null;
  const fftDisplacement = (px: ReturnType<typeof float>, pz: ReturnType<typeof float>) => {
    let dx: ReturnType<typeof float> = float(0);
    let dy: ReturnType<typeof float> = float(0);
    let dz: ReturnType<typeof float> = float(0);
    for (let i = 0; i < (fft?.displacement.length ?? 0); i++) {
      const d = texture(fft!.displacement[i], vec2(px, pz).div(fft!.patchSizes[i]));
      dx = dx.add(d.x);
      dy = dy.add(d.y);
      dz = dz.add(d.z);
    }
    return { dx, dy, dz };
  };

  /**
   * The displaced vertex for one wave evaluator. Called TWICE — once with the
   * current wave field and once one frame back — so the vertex stage can emit
   * a true previous position for the velocity buffer.
   *
   * The ripple and interaction TEXTURES are deliberately excluded from the
   * previous evaluation: they have no history to sample, and their amplitude
   * is small next to the swell, so treating them as static costs a little
   * under-blur on ripples and avoids inventing motion.
   */
  const displacedAt = (g: typeof gerstnerVertex, withTextures: boolean) => {
    const wx = positionLocal.x.add(uWorldOffset.x);
    const wz = positionLocal.z.add(uWorldOffset.y);
    const d = fft ? fftDisplacement(wx, wz) : g.displacement(wx, wz);
    let dy = d.dy;
    if (config.rippleTexture && withTextures) {
      dy = dy.add(texture(config.rippleTexture, vec2(uv().x, float(1).sub(uv().y))).r);
    }
    let dx = d.dx;
    let dz = d.dz;
    const depthV = bedDepthNode();
    if (shore && depthV) {
      // Shoaling: the whole displacement (waves + ripples) dies as the bed
      // rises. MUST mirror ShorelineField.attenuationAt — the CPU height
      // query applies the same smoothstep so buoyancy matches the mesh.
      const shoal = smoothstep(0, shore.shoalDepth, depthV);
      dx = dx.mul(shoal);
      dy = dy.mul(shoal);
      dz = dz.mul(shoal);
    }
    if (config.distanceFade) {
      // Horizon fade: the exponential grid is sparse out there — displacing
      // it would alias. The mesh is camera-centered, so local radius = view
      // distance.
      const dist = positionLocal.x.mul(positionLocal.x).add(positionLocal.z.mul(positionLocal.z)).sqrt();
      const fade = float(1).sub(smoothstep(config.distanceFade.start, config.distanceFade.end, dist));
      dx = dx.mul(fade);
      dy = dy.mul(fade);
      dz = dz.mul(fade);
    }
    if (interaction && withTextures) {
      // Wakes and wading rings ride on top of the (shoaled) swell — never
      // attenuated by the bed: they are what shallow water shows most.
      const iuv = interactionUv(wx, wz);
      dy = dy.add(texture(interaction.texture, iuv).r.mul(interactionEdge(iuv)));
    }
    return vec3(positionLocal.x.add(dx), positionLocal.y.add(dy), positionLocal.z.add(dz));
  };

  material.positionNode = Fn(() => {
    // The positionPrevious LAW: a material with its own positionNode MUST
    // emit its own previous position, or three leaves it at the undisplaced
    // geometry and every wave's velocity becomes its full displacement.
    positionPrevious.assign(displacedAt(gerstnerVertexPrev, false) as never);
    return displacedAt(gerstnerVertex, true);
  })();

  // ── Reflection sampler (optional) ────────────────────────────────────────
  let reflectionSampler: ReturnType<typeof reflector> | null = null;
  if (config.reflection) {
    // bounces:false — ONE scene render per body per FRAME. three's default
    // (bounces:true) re-renders on every render call AND lets a second
    // reflective body (lakes beside the sea) nest its own reflection render
    // inside ours: four extra scene renders a frame instead of two. A water
    // body never needs to show another water body's reflection.
    reflectionSampler = reflector({ bounces: false });
    reflectionSampler.reflector.resolutionScale = config.reflectionResolutionScale;
    // The reflector mirrors across the plane through its `target` whose
    // normal is the target's LOCAL +Z. This sheet's rotation is baked into
    // the geometry (rotateX(-π/2): local +Y is up), so an un-rotated target
    // under the mesh mirrors across a VERTICAL plane — the player saw their
    // own camera view pasted onto the sea, then black water the moment the
    // camera crossed to that plane's "facing away" side (2026-09-10). Orient
    // the target here so its +Z is the mesh's +Y; the owner still parents it
    // under the water mesh (`reflectorTarget`).
    reflectionSampler.target.rotation.x = -Math.PI / 2;
    reflectionSampler.target.name = 'WaterReflectorTarget';
  }

  // ── Fragment ─────────────────────────────────────────────────────────────
  material.colorNode = Fn(() => {
    const wx = positionWorld.x;
    const wz = positionWorld.z;

    // Macro normal from the wave field + two scrolling detail-normal samples.
    // FFT mode reads choppy-corrected slopes + Jacobian from the derivative
    // cascades; Gerstner mode evaluates the analytic sums.
    let fftSlopeX: ReturnType<typeof float> = float(0);
    let fftSlopeZ: ReturnType<typeof float> = float(0);
    let fftJacobian: ReturnType<typeof float> = float(1);
    if (fft) {
      for (let i = 0; i < fft.derivatives.length; i++) {
        const dv = texture(fft.derivatives[i], vec2(wx, wz).div(fft.patchSizes[i]));
        fftSlopeX = fftSlopeX.add(dv.x);
        fftSlopeZ = fftSlopeZ.add(dv.y);
        fftJacobian = fftJacobian.min(dv.z);
      }
    }
    const macroNormal = fft
      ? normalize(vec3(fftSlopeX.negate(), 1, fftSlopeZ.negate()))
      : gerstner.normal(wx, wz);
    const t = uTime.mul(config.normalSpeed);
    // De-tiling: rotate the second sample ~34° so its repeat lattice never
    // lines up with the first — two axis-aligned lattices at different
    // scales still beat into a readable world-aligned quilt; a rotated one
    // cannot (standard layered-detail-normal practice).
    const rotC = Math.cos(0.6);
    const rotS = Math.sin(0.6);
    type Vec2Expr = ReturnType<ReturnType<typeof vec2>['add']>;
    let uv1: Vec2Expr;
    let uv2: Vec2Expr;
    if (config.flow) {
      // Strip advection: metric mesh uv, scrolled toward +u (downstream) at
      // flow.speed m/s; the second sample rotated + drifting a little across.
      const fs = config.flow.uvScale ?? config.normalScale;
      const fu = uv().x.sub(uTime.mul(config.flow.speed));
      const fv = uv().y;
      uv1 = vec2(fu, fv).mul(fs).add(vec2(0, t.mul(0.3)));
      const frx = fu.mul(rotC).sub(fv.mul(rotS));
      const frz = fu.mul(rotS).add(fv.mul(rotC));
      uv2 = vec2(frx, frz).mul(fs * 2.7).add(vec2(t.negate().mul(0.2), t.mul(0.5)));
    } else {
      // Both detail layers drift along uDetailDrift (the owner points it
      // downwind); the second a little turned and faster so the two lattices
      // never move in lockstep. Default (1, 0.8) = the authored look.
      uv1 = vec2(wx, wz).mul(config.normalScale).add(uDetailDrift.mul(t));
      const rx = wx.mul(rotC).sub(wz.mul(rotS));
      const rz = wx.mul(rotS).add(wz.mul(rotC));
      const drift2 = vec2(
        uDetailDrift.x.mul(0.9).sub(uDetailDrift.y.mul(0.3)),
        uDetailDrift.y.mul(0.9).add(uDetailDrift.x.mul(0.3)),
      ).mul(1.1);
      uv2 = vec2(rx, rz).mul(config.normalScale * 2.7).add(drift2.mul(t));
    }
    const n1 = texture(config.normalMap, uv1).xyz.mul(2).sub(1);
    const n2 = texture(config.normalMap, uv2).xyz.mul(2).sub(1);
    // Detail maps are tangent-space on an (almost) up-facing plane: XZ perturb.
    // The second sample was fetched in the rotated frame — rotate its
    // perturbation back (inverse rotation) so ripple directions stay unbiased.
    const n2x = n2.x.mul(rotC).add(n2.z.mul(rotS));
    const n2z = n2.x.negate().mul(rotS).add(n2.z.mul(rotC));
    const detail = n1.xz.add(vec2(n2x, n2z)).mul(0.5);
    // Interactive ripples: gradient channels tilt the normal (linearized
    // n = (−dh/dx, 1, −dh/dz)).
    const rippleGrad = (config.rippleTexture
      ? texture(config.rippleTexture, vec2(uv().x, float(1).sub(uv().y))).gb
      : vec2(0, 0)) as ReturnType<typeof vec2>;
    // The camera-following interaction field: its gradient tilts the normal
    // the same way; its steepness raises foam (a wake's churn) below.
    let interGrad = vec2(0, 0).mul(1);
    if (interaction) {
      const iuv = interactionUv(wx, wz);
      interGrad = texture(interaction.texture, iuv).gb.mul(interactionEdge(iuv));
    }
    // Rain: Lagarde's growing rings, dense over the whole surface (the sim
    // field only carries the drops big enough to ring outward).
    const rain = config.rainRipples ? rainRingSlope(vec2(wx, wz), uRainIntensity as unknown as ReturnType<typeof float>) : null;
    const normal = normalize(vec3(
      macroNormal.x.add(detail.x.mul(0.35)).sub(rippleGrad.x).sub(interGrad.x).sub(rain ? rain.x : float(0)),
      macroNormal.y,
      macroNormal.z.add(detail.y.mul(0.35)).sub(rippleGrad.y).sub(interGrad.y).sub(rain ? rain.z : float(0)),
    ));

    const viewDir = normalize(cameraPosition.sub(positionWorld));

    // Schlick Fresnel, F0 = 0.02 (water IOR 1.33).
    const cosTheta = max(dot(normal, viewDir), 0);
    const fresnel = float(0.02).add(pow(float(1).sub(cosTheta), 5).mul(0.98));

    // Terrain-aware bed depth from the baked ShorelineField (plane UV,
    // v flipped like the foam textures). Read EARLY: it also hardens the
    // water-thickness estimate below.
    const bedDepth = bedDepthNode();

    // Waterline discard: the depth-grid geometry interpolates surface height
    // across its edge quads, so on steep shores the sheet's rim hangs over
    // the ground — carve the surface along the BILINEAR zero-depth contour
    // (~5 cm of column) instead. A fragment Discard INSIDE the color node:
    // material.alphaTest would move the water into the alpha-tested pass
    // family, where its scene-copy node ran against mismatched render-target
    // formats on WebGPU (rgba16float vs bgra8unorm copy spam → destroyed-
    // texture cascade, 2026-07-30 console).
    // A strip (attribute depth) keeps its lip unless asked to carve.
    const waterlineDiscard = shore?.waterlineDiscard ?? !shore?.depthAttribute;
    if (bedDepth && waterlineDiscard) Discard(bedDepth.lessThan(0.05));

    // View-space water thickness from the depth buffer: scene behind the
    // surface minus this fragment (Sims 2021 / Cyanilux depth-fade).
    // WHY the geometric term: the depth-buffer difference silently reads ~0
    // whenever the floor isn't in the rasterized viewport copy (off-screen
    // floor, backend depth-copy limits) — absorption then never absorbs, the
    // shoreline foam band covers the WHOLE surface, and caustics never
    // attenuate with depth. When the baked bed field exists, the slant path
    // length through the column (bedDepth / cosθ) is a floor-independent
    // lower bound — take the max of both estimates.
    const depthThickness = max(viewportLinearDepth.sub(linearDepth()), 0);
    const thickness = bedDepth
      ? max(depthThickness, bedDepth.div(max(cosTheta, 0.25)))
      : depthThickness;

    // Screen-space refraction — distortion eases to zero in shallow water so
    // the shoreline doesn't smear (Water2Mesh idiom + depth gate).
    const shallowEase = smoothstep(0, 0.35, thickness);
    const distortion = normal.xz.mul(config.distortionScale).mul(shallowEase);
    // No-copy fallback: the floor reads as sun-lit shallow color — absorption
    // below still converges it to deepColor with the column, so the sheet
    // keeps the classic depth gradient without ever touching the framebuffer.
    const refractedScene = config.screenSpaceRefraction !== false
      ? adaptiveSceneColor(viewportSafeUV(screenUV.add(distortion))).rgb
      : color(new THREE.Color(config.shallowColor)).mul(uIlluminance);

    // Beer–Lambert per-channel transmittance over the water column;
    // reddish extinction ⇒ light goes teal→deep blue with depth.
    const sigma = vec3(config.absorption.r, config.absorption.g, config.absorption.b);
    const transmittance = exp(sigma.negate().mul(thickness));

    // Caustics on the refracted floor: the PROJECTED light map when the
    // CausticsProjector runs (real refracted-ray convergence), else the
    // animated Voronoi approximation (GPU Gems ch.2 style).
    const caustic = config.causticTexture
      ? texture(config.causticTexture, vec2(uv().x, float(1).sub(uv().y))).r.mul(config.causticIntensity)
      : (() => {
          const cUv = vec3(wx.mul(config.causticScale), wz.mul(config.causticScale), uTime.mul(0.35));
          const v1 = mx_worley_noise_float(cUv);
          const v2 = mx_worley_noise_float(cUv.mul(1.9).add(vec3(3.1, 5.7, 0)));
          return pow(float(1).sub(v1.mul(v2)), 6).mul(config.causticIntensity);
        })();
    const floorLit = refractedScene.add(caustic.mul(uIlluminance).mul(transmittance.g).mul(shallowEase));

    // Underwater color: floor light absorbed by the column, converging to
    // deepColor; a shallow tint scatters in near the top of the column.
    // The AUTHORED radiance terms (deep scatter, flat sky, foam, caustics,
    // glint) scale by uIlluminance so the water follows the day/night cycle —
    // static day values read as a glowing sea at midnight (the same
    // daylight-authored-constant family as the fog and cloud night bugs).
    const shallow = color(new THREE.Color(config.shallowColor));
    const deep = color(new THREE.Color(config.deepColor)).mul(uIlluminance);
    let underwater = mix(deep, floorLit.mul(mix(shallow, vec3(1), transmittance)), transmittance);

    if (config.shoreline && bedDepth && config.shoreline.shallowTint !== null) {
      // Sand shows through as the bed nears the surface — view-independent
      // (the depth-buffer term above needs the floor rasterized on screen).
      const sand = color(new THREE.Color(config.shoreline.shallowTint));
      // Narrower + weaker than the original (·0.6 span, 0.65 mix): on wide
      // gentle beaches the old values painted the whole surf zone as flat
      // DRY sand at grazing angles instead of water over sand.
      const sandBlend = float(1).sub(smoothstep(0, config.shoreline.shoalDepth * 0.45, bedDepth));
      underwater = mix(underwater, floorLit.mul(sand), sandBlend.mul(0.45)) as typeof underwater;
    }

    // Sky/reflection side of the Fresnel blend.
    let skySide;
    if (reflectionSampler) {
      // Perturb the planar-reflection lookup by the surface normal
      // (Water2Mesh idiom: uvNode is pre-populated by reflector()).
      if (reflectionSampler.uvNode) {
        reflectionSampler.uvNode = reflectionSampler.uvNode.add(normal.xz.mul(0.06));
      }
      skySide = reflectionSampler.rgb;
    } else if (config.envMap) {
      // IBL fallback: PMREM environment sampled along the wavy reflection
      // vector, blurred by the surface roughness.
      const reflectDir = normalize(reflect(viewDir.negate(), normal));
      skySide = pmremTexture(config.envMap, reflectDir, float(config.sunRoughness)).rgb;
    } else {
      // Flat fallback: a UNIFORM (not a baked constant) so the owner can tint
      // it with the time of day — a static day-blue reflection side makes the
      // sea ignore sunsets and glow at night. Illumination-scaled like every
      // authored radiance term (reflector/envMap paths self-dim).
      skySide = vec3(uSkyColor).mul(uIlluminance);
    }

    // Sun glint — GGX microfacet (Cook–Torrance): D·G·F/(4·NV·NL)·NL.
    // The GGX tail is what stretches sun glitter at grazing angles; Blinn
    // pow() cuts it off unnaturally early on rough water.
    const alpha = Math.max(0.02, config.sunRoughness) ** 2;
    const halfway = normalize(uSunDirection.add(viewDir));
    const ndh = max(dot(normal, halfway), 0);
    const ndv = max(dot(normal, viewDir), 1e-4);
    const ndl = max(dot(normal, uSunDirection), 1e-4);
    const denom = ndh.mul(ndh).mul(alpha * alpha - 1).add(1);
    const distGgx = float(alpha * alpha).div(denom.mul(denom).mul(Math.PI));
    const kGeom = alpha / 2;
    const gView = ndv.div(ndv.mul(1 - kGeom).add(kGeom));
    const gLight = ndl.div(ndl.mul(1 - kGeom).add(kGeom));
    const fSpec = float(0.02).add(pow(float(1).sub(max(dot(halfway, viewDir), 0)), 5).mul(0.98));
    const glint = distGgx
      .mul(gView)
      .mul(gLight)
      .mul(fSpec)
      .div(ndv.mul(ndl).mul(4).max(1e-4))
      .mul(ndl)
      .mul(color(new THREE.Color(config.sunColor)))
      .mul(2.2)
      .mul(uIlluminance)
      // Firefly cap: choppy per-pixel normals at steep views make the GGX
      // spike into isolated saturated dashes; real glitter clips at the
      // sensor. 6× keeps the grazing sparkle stretch, kills the confetti.
      .min(float(6));

    // Foam: shoreline band (thin water) + Jacobian whitecaps (surface
    // compression at folding crests) + lingering trails from the accumulator,
    // plus the legacy crest-height term, all broken up by animated noise.
    const shoreFoam = float(1).sub(smoothstep(0, config.foamShoreDepth, thickness));
    const foamNoise = mx_noise_float(vec3(wx.mul(1.4), wz.mul(1.4), uTime.mul(0.5))).mul(0.5).add(0.5);
    const crestFoam = fft
      ? float(0) // the Jacobian term below carries FFT whitecaps
      : smoothstep(
          ampScaleNode.mul(config.foamCrestStart * totalAmp),
          ampScaleNode.mul(totalAmp * 0.98),
          gerstner.displacement(wx, wz).dy,
        );
    // The whitecap threshold is a uniform: a gale raises it (more crests
    // break), a calm lowers it — initialised from the authored constant.
    const jThr = uFoamJacobian;
    // Ramps 0 → 1 as J falls from the threshold toward folding.
    const jacobianFoam = float(1).sub(
      smoothstep(jThr.sub(0.35), jThr, fft ? fftJacobian : gerstner.jacobian(wx, wz)),
    );
    // Churn foam from the interaction field's steep slopes (a hull's wake,
    // a splash crown) — wading rings are too gentle to whiten.
    const wakeFoam = interaction
      ? smoothstep(0.06, 0.32, length(interGrad)).mul(config.interactionFoam ?? 0.6)
      : float(0);
    // Accumulated trails live in plane UV; v flips (plane rotateX maps z = −y).
    // pow-shaping thins the accumulator's coarse (metres-scale) texels into
    // froth instead of solid cotton blobs — the grid is CPU-priced, so its
    // resolution can't simply be raised.
    const trailFoam = config.foamAccumTexture
      ? pow(texture(config.foamAccumTexture, vec2(uv().x, float(1).sub(uv().y))).r, 1.5)
      : float(0);
    const whitecaps = max(jacobianFoam, trailFoam.mul(0.8));
    // Surf band from the baked bed depth: foam hugs the beach from ANY view
    // angle, pulsing gently with the (shoaled) wave phase.
    const beachFoam = bedDepth && config.shoreline
      ? float(1).sub(smoothstep(0, config.shoreline.foamDepth, bedDepth)).mul(0.9)
      : float(0);
    const foamMask = clamp(shoreFoam.add(whitecaps).add(crestFoam.mul(0.5)).add(beachFoam).add(wakeFoam), 0, 1)
      .mul(smoothstep(0.2, 0.7, foamNoise.add(whitecaps.mul(0.35)).add(beachFoam.mul(0.3)).add(wakeFoam.mul(0.5))));

    const water = mix(underwater, skySide, fresnel.mul(0.9)).add(glint);
    const aboveColor = mix(water, color(new THREE.Color(config.foamColor)).mul(uIlluminance), foamMask);

    // ── Underside (camera submerged, looking up): Snell's window ─────────────
    // Incident ray direction camera → fragment; its angle to the vertical
    // decides transmission (window) vs total internal reflection.
    const incident = normalize(positionWorld.sub(cameraPosition));
    const cosUp = clamp(dot(incident, normal), 0, 1);
    // cos θc ≈ 0.66 — a soft band around it avoids a hard window rim.
    const windowMask = smoothstep(0.6, 0.72, cosUp);
    // Inside the window: the raster behind the surface IS the above-water
    // scene from a submerged camera — refract it through the wavy normal.
    // No-copy fallback: the world above ≈ the flat sky term.
    const skyThrough = config.screenSpaceRefraction !== false
      ? adaptiveSceneColor(
          viewportSafeUV(screenUV.add(normal.xz.mul(config.distortionScale * 1.6))),
        ).rgb.mul(1.06)
      : color(new THREE.Color(config.skyColor)).mul(uIlluminance).mul(1.06);
    // Beyond the critical angle: TIR — the surface mirrors the dark water
    // body itself (deep color, slightly modulated so it isn't a flat sheet).
    const tir = deep.mul(float(0.55).add(cosUp.mul(0.25)));
    const belowColor = mix(tir, skyThrough, windowMask).add(glint.mul(windowMask));

    return vec4(mix(belowColor, aboveColor, float(frontFacing)), 1);
  })();

  // Water writes depth normally (it's opaque; refraction fakes the see-through).
  // DoubleSide: the underside renders Snell's window for submerged cameras.
  material.transparent = false;
  material.side = THREE.DoubleSide;



  return {
    material,
    uTime,
    uFrameDt,
    uSunDirection,
    uWorldOffset,
    uIlluminance,
    uSkyColor,
    uWaveAmpScale,
    uWaveDirRot,
    waveModulationNodes: modNodes,
    uInteractionCenter,
    uFoamJacobian,
    uDetailDrift,
    uRainIntensity,
    reflectorTarget: reflectionSampler ? reflectionSampler.target : null,
    reflectorNode: reflectionSampler,
  };
}
